import { Fragment, useEffect, useRef, useState } from 'react';
import { Button, DialogContent, Grid2, IconButton, Stack, Typography } from '@mui/material';

import { API_DATE_FORMAT, formatDate } from 'helper/DateLib';
import { notifyApiError } from 'helper/apiErrorNotification';
import { useDialog } from 'hooks/useDialog';
import { useBreakpointFlags } from 'hooks/useBreakpointFlags';
import { useMatchPathParams } from 'hooks/useMatchPathParams';
import {
  useAddKeyEventMutation,
  useDeleteKeyEventMutation,
  useGetKeyEventsLimitsQuery,
  useGetKeyEventsQuery,
  useUpdateKeyEventMutation,
} from 'store/entities/projects/api';
import { TitleTooltip } from 'components/simple';
import { Dialog } from 'components/complex';
import { DeleteIcon, EditIcon } from 'assets/icons';

import { KeyEventForm } from './KeyEventForm';
import { DeletedKeyEventConfirmation } from './DeletedKeyEventConfirmation';

const defaultKeyEventValues = {
  name: '',
  date: null,
};

const TABLE_ROWS_GAP = 16;
const TABLE_ROW_HEIGHT = 50;

const TABLE_CELL_KEYS = {
  NAME: 'name',
  DATE: 'date',
  ACTIONS: 'actions',
};

const tableHeaders = [
  {
    key: TABLE_CELL_KEYS.NAME,
    name: 'Key event name',
    gridItemProps: { size: 6 },
  },
  {
    key: TABLE_CELL_KEYS.DATE,
    name: 'Event date',
    gridItemProps: { size: 4 },
  },
  {
    key: TABLE_CELL_KEYS.ACTIONS,
    name: 'Actions',
    gridItemProps: { size: 2 },
  },
];

const styles = {
  paper: ({ breakpoints }) => ({
    minHeight: 400,
    [breakpoints.up('xl')]: {
      maxWidth: 1104,
    },
  }),
  dialogContent: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    overflowY: 'visible',
  },
  iconButton: ({ palette }) => ({
    padding: 'unset',
    color: palette.text.primary,
    svg: {
      width: 16,
      height: 16,
    },
  }),
  addItemButton: ({ palette }) => ({
    alignSelf: 'flex-start',
    color: `${palette.primary.main} !important`,
  }),
  tableContainer: ({ breakpoints }) => ({
    gap: `${TABLE_ROWS_GAP}px`,
    height: 'auto',
    minHeight: 200,
    maxHeight: 300,
    overflowY: 'auto',
    [breakpoints.up('md')]: {
      maxHeight: 400,
    },
    [breakpoints.up('xxl')]: {
      maxHeight: 500,
    },
  }),
  tableRow: ({ palette }) => ({
    height: TABLE_ROW_HEIGHT,
    paddingBlock: 2,
    paddingInline: 4,
    backgroundColor: palette.colors.lightPale,
    borderRadius: 2.5,
  }),
  tableHeaderRow: ({ palette }) => ({
    backgroundColor: palette.primary.main,
  }),
  tableCell: {
    display: 'flex',
    alignItems: 'center',
  },
  actionsTableCell: {
    justifyContent: 'flex-end',
  },
};

const getRowId = (keyEventId) => (keyEventId ? `row_${keyEventId}` : null);

export const KeyEventsDialog = () => {
  const { closeDialog } = useDialog();
  const { id: projectId } = useMatchPathParams();

  const { getResponsiveValueForCurrent } = useBreakpointFlags();
  const keyEventTitleLength = getResponsiveValueForCurrent({
    xs: 20,
    sm: 25,
    md: 25,
    lg: 30,
    xl: 40,
    xxl: 40,
  });

  const { data: keyEventsLimits = [], isLoading: isLoadingKeyEventsLimits } = useGetKeyEventsLimitsQuery({ projectId });
  const { data: keyEventsList = [], isLoading: isLoadingKeyEventsList } = useGetKeyEventsQuery({ projectId });

  const [addKeyEvent] = useAddKeyEventMutation();
  const [updateKeyEvent] = useUpdateKeyEventMutation();
  const [deleteKeyEvent, { isLoading: isDeletingKeyEvent }] = useDeleteKeyEventMutation();

  const [deletedKeyEvent, setDeletedKeyEvent] = useState(null);
  const [selectedKeyEvent, setSelectedKeyEvent] = useState();
  const tableRef = useRef(null);

  const defaultEditedKeyEvent = keyEventsList.length ? null : defaultKeyEventValues;
  const editedKeyEvent = selectedKeyEvent === undefined ? defaultEditedKeyEvent : selectedKeyEvent;

  const keyEventsTableRows = keyEventsList.map((keyEvent) => {
    const isDisabledRowButtons = !!editedKeyEvent || !!deletedKeyEvent;

    return {
      id: keyEvent.id,
      cells: [
        {
          key: TABLE_CELL_KEYS.NAME,
          value: keyEvent.name,
          gridItemProps: { size: 6 },
        },
        {
          key: TABLE_CELL_KEYS.DATE,
          value: formatDate(keyEvent.date),
          gridItemProps: { size: 4 },
        },
        {
          key: TABLE_CELL_KEYS.ACTIONS,
          gridItemProps: { size: 2 },
          value: (
            <Stack direction="row" spacing={3}>
              <IconButton
                size="small"
                onClick={() =>
                  setSelectedKeyEvent({
                    ...keyEvent,
                    date: new Date(keyEvent.date),
                  })
                }
                disabled={isDisabledRowButtons}
                sx={styles.iconButton}
              >
                <EditIcon />
              </IconButton>

              <IconButton
                size="small"
                onClick={() => setDeletedKeyEvent(keyEvent)}
                disabled={isDisabledRowButtons}
                sx={styles.iconButton}
              >
                <DeleteIcon />
              </IconButton>
            </Stack>
          ),
        },
      ],
    };
  });

  const onSaveKeyEvent = async (keyEventValues, keyEventId) => {
    const data = {
      name: keyEventValues.name,
      date: formatDate(keyEventValues.date, API_DATE_FORMAT),
      project: projectId,
    };

    try {
      if (keyEventId) {
        await updateKeyEvent({ keyEventId, data }).unwrap();
      } else {
        await addKeyEvent(data).unwrap();
      }

      setSelectedKeyEvent(null);
    } catch (error) {
      notifyApiError(error);
    }
  };

  const onDeleteKeyEvent = async (keyEventId) => {
    try {
      await deleteKeyEvent({ keyEventId }).unwrap();
      setDeletedKeyEvent(null);
    } catch (error) {
      notifyApiError(error);
    }
  };

  useEffect(() => {
    const selectedRowId = editedKeyEvent?.id || deletedKeyEvent?.id;

    if (selectedRowId) {
      const rowContent = document.getElementById(getRowId(selectedRowId));
      const tableNode = tableRef.current;

      tableNode.scroll({
        top: rowContent.offsetTop - (TABLE_ROWS_GAP + TABLE_ROW_HEIGHT) * 2,
        behavior: 'smooth',
      });
    }
  }, [deletedKeyEvent, editedKeyEvent]);

  if (isLoadingKeyEventsLimits || isLoadingKeyEventsList) {
    return null;
  }

  return (
    <Dialog
      withCustomContent
      onClose={closeDialog}
      id="key-events-dialog"
      title="Key Events"
      customStyles={{ paperStyles: styles.paper }}
    >
      <DialogContent sx={styles.dialogContent}>
        <Stack spacing={3}>
          {!!keyEventsTableRows.length && (
            <Stack sx={styles.tableContainer} ref={tableRef}>
              <Grid2 container sx={[styles.tableRow, styles.tableHeaderRow]}>
                {tableHeaders.map(({ key, name, gridItemProps }) => (
                  <Grid2
                    key={`header-${key}`}
                    {...gridItemProps}
                    sx={[styles.tableCell, key === TABLE_CELL_KEYS.ACTIONS && styles.actionsTableCell]}
                  >
                    <Typography variant="body2" color="text.secondary">
                      {name}
                    </Typography>
                  </Grid2>
                ))}
              </Grid2>

              {keyEventsTableRows.map(({ id, cells }) => {
                const rowId = getRowId(id);
                const isEdited = editedKeyEvent?.id === id;
                const isDeleted = deletedKeyEvent?.id === id;

                return (
                  <Fragment key={rowId}>
                    {isEdited && (
                      <KeyEventForm
                        formId={rowId}
                        keyEventsLimits={keyEventsLimits}
                        defaultValues={editedKeyEvent}
                        onSubmit={(values) => onSaveKeyEvent(values, editedKeyEvent.id)}
                        onCancel={() => setSelectedKeyEvent(null)}
                      />
                    )}

                    {isDeleted && (
                      <DeletedKeyEventConfirmation
                        isLoading={isDeletingKeyEvent}
                        rowId={rowId}
                        onCancel={() => setDeletedKeyEvent(null)}
                        onConfirm={() => onDeleteKeyEvent(deletedKeyEvent.id)}
                      />
                    )}

                    {!isEdited && !isDeleted && (
                      <Grid2 container sx={styles.tableRow}>
                        {cells.map(({ key, value, gridItemProps }) => (
                          <Grid2
                            key={`${rowId}-${key}`}
                            {...gridItemProps}
                            sx={[styles.tableCell, key === TABLE_CELL_KEYS.ACTIONS && styles.actionsTableCell]}
                          >
                            {key === TABLE_CELL_KEYS.NAME ? (
                              <TitleTooltip titleLength={keyEventTitleLength} title={value} variant="body2" />
                            ) : (
                              value
                            )}
                          </Grid2>
                        ))}
                      </Grid2>
                    )}
                  </Fragment>
                );
              })}
            </Stack>
          )}

          {(!editedKeyEvent || editedKeyEvent.id) && (
            <Button
              variant="text"
              disabled={!!editedKeyEvent}
              onClick={() => setSelectedKeyEvent(defaultKeyEventValues)}
              sx={styles.addItemButton}
            >
              + Add key event
            </Button>
          )}

          {!!editedKeyEvent && !editedKeyEvent.id && (
            <KeyEventForm
              defaultValues={editedKeyEvent}
              keyEventsLimits={keyEventsLimits}
              onSubmit={(values) => onSaveKeyEvent(values)}
              onCancel={() => {
                if (!keyEventsTableRows.length) {
                  closeDialog();
                } else {
                  setSelectedKeyEvent(null);
                }
              }}
            />
          )}
        </Stack>
      </DialogContent>
    </Dialog>
  );
};
